import { Component, OnInit } from '@angular/core';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { AffiliateDashboardI, AffiliateRewardsInfoI, AffiliateTierI, EmailNotificationsI } from 'src/app/interfaces/affiliate.interface';
import { ReferredInstanceI, ReferredInstancesResponseI, ReferralStatisticsResponseI } from 'src/app/interfaces/referrals.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-affiliate',
  templateUrl: './affiliate.page.html',
  styleUrls: ['./affiliate.page.scss']
})
export class AffiliatePage implements OnInit {

  isLoading = true;
  dashboard: AffiliateDashboardI | null = null;
  rewardsInfo: AffiliateRewardsInfoI | null = null;
  referredInstances: ReferredInstanceI[] = [];
  statistics: ReferralStatisticsResponseI | null = null;

  // Tabs
  activeTab: 'overview' | 'referrals' | 'settings' = 'overview';

  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalInstances = 0;
  pageSize = 10;

  // Copy feedback
  copied = false;

  // Email notifications
  emailNotifications: EmailNotificationsI = {
    onReferralSignup: true,
    onReferralConversion: true
  };
  notificationsSaved = false;

  // Tier order for comparison
  private tierOrder: AffiliateTierI[] = ['bronze', 'silver', 'gold', 'diamond'];

  constructor(
    private apiSdkService: ApiSdkService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.isLoading = true;
    try {
      const [dashboardRes, rewardsRes, instancesRes, statsRes] = await Promise.all([
        this.apiSdkService.getAffiliateDashboard(),
        this.apiSdkService.getAffiliateRewardsInfo(),
        this.apiSdkService.getReferredInstances({ skip: 0, limit: this.pageSize }),
        this.apiSdkService.getReferralStatistics()
      ]);

      if (dashboardRes?.success?.data) {
        this.dashboard = dashboardRes.success.data;
        // Set email notification preferences
        if (this.dashboard.emailNotifications) {
          this.emailNotifications = this.dashboard.emailNotifications;
        }
      }
      if (rewardsRes?.success?.data) {
        this.rewardsInfo = rewardsRes.success.data;
      }
      if (instancesRes?.success?.data) {
        this.referredInstances = instancesRes.success.data.instances;
        this.totalInstances = instancesRes.success.data.total;
        this.totalPages = instancesRes.success.data.totalPages;
      }
      if (statsRes?.success?.data) {
        this.statistics = statsRes.success.data;
      }
    } catch (error) {
      console.error('Error loading affiliate data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    const skip = (page - 1) * this.pageSize;

    try {
      const instancesRes = await this.apiSdkService.getReferredInstances({ skip, limit: this.pageSize });
      if (instancesRes?.success?.data) {
        this.referredInstances = instancesRes.success.data.instances;
      }
    } catch (error) {
      console.error('Error loading page:', error);
    }
  }

  async copyReferralLink() {
    if (!this.dashboard?.referralLink) return;

    try {
      await navigator.clipboard.writeText(this.dashboard.referralLink);
      this.copied = true;
      setTimeout(() => this.copied = false, 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  }

  shareViaWhatsApp() {
    if (!this.dashboard?.referralLink) return;
    const text = encodeURIComponent(`Check out Totalum - an amazing AI-powered app builder! Sign up with my link and get 150 bonus credits + 10% off your first subscription: ${this.dashboard.referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  }

  shareViaTwitter() {
    if (!this.dashboard?.referralLink) return;
    const text = encodeURIComponent(`Build amazing apps with AI using @TotalumApp! Sign up with my link and get bonus credits:`);
    const url = encodeURIComponent(this.dashboard.referralLink);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  }

  shareViaLinkedIn() {
    if (!this.dashboard?.referralLink) return;
    const url = encodeURIComponent(this.dashboard.referralLink);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  }

  getTierIcon(tier: AffiliateTierI): string {
    const icons: Record<AffiliateTierI, string> = {
      bronze: 'shield-outline',
      silver: 'shield',
      gold: 'medal',
      diamond: 'diamond'
    };
    return icons[tier] || 'shield-outline';
  }

  getTierFaIcon(tier: AffiliateTierI): string {
    const icons: Record<AffiliateTierI, string> = {
      bronze: 'fa-shield',
      silver: 'fa-shield-halved',
      gold: 'fa-medal',
      diamond: 'fa-gem'
    };
    return icons[tier] || 'fa-shield';
  }

  getTierColor(tier: AffiliateTierI): string {
    const colors: Record<AffiliateTierI, string> = {
      bronze: '#cd7f32',
      silver: '#a8a9ad',
      gold: '#ffd700',
      diamond: '#b9f2ff'
    };
    return colors[tier] || '#cd7f32';
  }

  isTierAchieved(tier: AffiliateTierI): boolean {
    if (!this.dashboard) return false;
    const currentTierIndex = this.tierOrder.indexOf(this.dashboard.tier.current);
    const checkTierIndex = this.tierOrder.indexOf(tier);
    return checkTierIndex <= currentTierIndex;
  }

  getMilestoneProgress(): number {
    if (!this.dashboard || !this.rewardsInfo) return 0;
    const { convertedReferrals } = this.dashboard.stats;
    const cycleLength = this.rewardsInfo.milestoneThresholds.diamond; // 31
    const positionInCycle = convertedReferrals % cycleLength;

    // Calculate progress to next milestone
    const thresholds = this.rewardsInfo.milestoneThresholds;
    let nextThreshold: number;

    if (positionInCycle < thresholds.silver) {
      nextThreshold = thresholds.silver;
    } else if (positionInCycle < thresholds.gold) {
      nextThreshold = thresholds.gold;
    } else {
      nextThreshold = thresholds.diamond;
    }

    return (positionInCycle / nextThreshold) * 100;
  }

  getMilestoneProgressPercentage(): number {
    if (!this.dashboard || !this.rewardsInfo) return 0;
    const { convertedReferrals } = this.dashboard.stats;
    const thresholds = this.rewardsInfo.milestoneThresholds;

    // Calculate position in cycle
    const cycleLength = thresholds.diamond;
    const positionInCycle = convertedReferrals % cycleLength || (convertedReferrals > 0 ? cycleLength : 0);

    // Calculate percentage across the timeline (0 -> silver -> gold -> diamond)
    if (positionInCycle === 0 && convertedReferrals === 0) {
      return 0;
    } else if (positionInCycle <= thresholds.silver) {
      // Between start and silver (0% to 33%)
      return (positionInCycle / thresholds.silver) * 33;
    } else if (positionInCycle <= thresholds.gold) {
      // Between silver and gold (33% to 66%)
      return 33 + ((positionInCycle - thresholds.silver) / (thresholds.gold - thresholds.silver)) * 33;
    } else {
      // Between gold and diamond (66% to 100%)
      return 66 + ((positionInCycle - thresholds.gold) / (thresholds.diamond - thresholds.gold)) * 34;
    }
  }

  getCreditsPerPlanValue(plan: string): number {
    if (!this.rewardsInfo) return 0;
    return this.rewardsInfo.creditsPerPlan[plan as keyof typeof this.rewardsInfo.creditsPerPlan] || 0;
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getPlanBadgeClass(plan: string): string {
    const classes: Record<string, string> = {
      free: 'badge-free',
      starter: 'badge-starter',
      shared: 'badge-shared',
      profesional: 'badge-professional',
      enterprise: 'badge-enterprise'
    };
    return classes[plan] || 'badge-free';
  }

  async toggleNotification(type: 'onReferralSignup' | 'onReferralConversion') {
    this.emailNotifications[type] = !this.emailNotifications[type];

    try {
      await this.apiSdkService.updateAffiliateNotifications(this.emailNotifications);
      this.notificationsSaved = true;
      setTimeout(() => this.notificationsSaved = false, 2000);
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      // Revert the change on error
      this.emailNotifications[type] = !this.emailNotifications[type];
    }
  }
}
